import { memo } from 'react';

import { BoardAddIcon, FormIcon, MousePointerClickIcon, SquareSplitHorizontalIcon, TagsIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import { AgentDropdownMenu } from '~/components/v2/components/private-selection-bar/agent-selection-bar/agent-dropdown-menu/AgentDropdownMenu';
import { renderAgentDropdownItems } from '~/components/v2/components/private-selection-bar/agent-selection-bar/agent-dropdown-menu/renderAgentDropdownItems';
import { type SelectionBarActions } from '~/components/v2/components/shared/selectionBar/selectionBarActions';

export type AgentSelectionBarPrefixActionsProps = {
  actions: SelectionBarActions;
};

/** Prod prefix icon order: Compare → Edit tags → Edit in Canvas → Add to board → Custom fields. Favorite lives in the ellipsis menu. */
export const AgentSelectionBarPrefixActions = memo(({ actions }: AgentSelectionBarPrefixActionsProps) => (
  <>
    {actions.compare && (
      <Tooltip label="Compare">
        <IconButton
          adsIcon
          appearance="ghost"
          className="shrink-0 rounded-6 bg-transparent ads:text-inverse/75 text-white/75 hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
          data-testid="SELECTION_ACTION_BAR_COMPARE"
          icon={SquareSplitHorizontalIcon}
          label="Compare"
          onClick={actions.compare.onSelect}
          size="small"
        />
      </Tooltip>
    )}

    {actions.editTags && (
      <Tooltip label="Edit tags">
        <IconButton
          adsIcon
          appearance="ghost"
          className="shrink-0 rounded-6 bg-transparent ads:text-inverse/75 text-white/75 hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
          data-testid="SELECTION_ACTION_BAR_EDIT_TAGS"
          label="Edit tags"
          onClick={actions.editTags.onSelect}
          size="small"
          icon={TagsIcon}
        />
      </Tooltip>
    )}

    {actions.editInCanvas && actions.editInCanvas.options.length > 0 && (
      <AgentDropdownMenu
        data-testid="AGENT_SELECTION_BAR_EDIT_IN_CANVAS_DROPDOWN_MENU"
        trigger={
          <IconButton
            adsIcon
            appearance="ghost"
            className="shrink-0 rounded-6 bg-transparent ads:text-inverse/75 text-white/75 hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
            data-testid="SELECTION_ACTION_BAR_EDIT_IN_CANVAS"
            label="Edit in Canvas"
            size="small"
            icon={MousePointerClickIcon}
          />
        }
        triggerTooltip="Edit in Canvas"
      >
        {renderAgentDropdownItems({ options: actions.editInCanvas.options })}
      </AgentDropdownMenu>
    )}

    {actions.addToBoard && (
      <Tooltip label="Add to board">
        <IconButton
          adsIcon
          appearance="ghost"
          className="shrink-0 rounded-6 bg-transparent ads:text-inverse/75 text-white/75 hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
          data-testid="SELECTION_ACTION_BAR_ADD_TO_BOARD"
          label="Add to board"
          onClick={actions.addToBoard.onSelect}
          size="small"
          icon={BoardAddIcon}
        />
      </Tooltip>
    )}

    {actions.customFields && actions.customFields.options.length > 0 && (
      <AgentDropdownMenu
        data-testid="AGENT_SELECTION_BAR_CUSTOM_FIELDS_DROPDOWN_MENU"
        trigger={
          <IconButton
            adsIcon
            appearance="ghost"
            className="shrink-0 rounded-6 bg-transparent ads:text-inverse/75 text-white/75 hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
            data-testid="SELECTION_ACTION_BAR_CUSTOM_FIELDS"
            label="Custom fields"
            size="small"
            icon={FormIcon}
          />
        }
        triggerTooltip="Custom fields"
      >
        {renderAgentDropdownItems({ options: actions.customFields.options })}
      </AgentDropdownMenu>
    )}
  </>
));

AgentSelectionBarPrefixActions.displayName = 'AgentSelectionBarPrefixActions';
